import React, { useState, useEffect } from 'react';
import { Upload, Database, FileSpreadsheet, CheckCircle, AlertCircle, Download, Trash2, RefreshCw } from 'lucide-react';

export default function BiharDataImport() {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [mongoConfig, setMongoConfig] = useState({
    uri: 'mongodb://bihar_admin:bihar2024@65.109.9.212:27017',
    database: 'bihar_sports',
    collection: 'athletes'
  });

  // Load datasets on mount and after successful import
  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoadingDatasets(true);
    try {
      const response = await fetch('/api/bihar-datasets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list' })
      });
      const data = await response.json();
      if (data.success) {
        setDatasets(data.datasets || []);
      }
    } catch (error) {
      console.error('Failed to load datasets:', error);
    } finally {
      setLoadingDatasets(false);
    }
  };

  const deleteDataset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this dataset?')) return;
    
    try {
      const response = await fetch('/api/bihar-datasets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id })
      });
      const data = await response.json();
      if (data.success) {
        loadDatasets(); // Refresh list
      }
    } catch (error) {
      console.error('Failed to delete dataset:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setResult(null);

    try {
      // Read file as base64
      const reader = new FileReader();
      const fileData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send as JSON with base64 data
      const response = await fetch('/api/bihar-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileData: fileData.split(',')[1], // Remove data:...;base64, prefix
          fileType: file.type
        })
      });

      // Check if response is OK
      if (!response.ok) {
        const text = await response.text();
        console.error('Server error:', response.status, text);
        setResult({ 
          success: false, 
          error: `Server error (${response.status}): ${text.substring(0, 200)}` 
        });
        setImporting(false);
        return;
      }

      const data = await response.json();
      setResult(data);
      
      // Reload datasets after successful import
      if (data.success) {
        setTimeout(() => loadDatasets(), 1000);
      }
    } catch (error) {
      console.error('Import error:', error);
      setResult({ success: false, error: error.message });
    } finally {
      setImporting(false);
    }
  };

  const handleMongoConnect = async () => {
    setImporting(true);
    setResult(null);

    try {
      const response = await fetch('/api/bihar-import-mongo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mongoConfig)
      });

      // Check if response is OK
      if (!response.ok) {
        const text = await response.text();
        console.error('MongoDB import error:', response.status, text);
        setResult({ 
          success: false, 
          error: `Server error (${response.status}): ${text.substring(0, 200)}` 
        });
        setImporting(false);
        return;
      }

      const data = await response.json();
      setResult(data);
      
      // Reload datasets after successful import
      if (data.success) {
        setTimeout(() => loadDatasets(), 1000);
      }
    } catch (error) {
      console.error('MongoDB import error:', error);
      setResult({ success: false, error: error.message });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-green-600/20 rounded-lg">
            <Upload className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Data Import</h2>
            <p className="text-slate-400">Import athlete data from Excel/CSV or MongoDB</p>
          </div>
        </div>
      </div>

      {/* Import Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Excel/CSV Upload */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Excel / CSV Upload</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Upload an Excel (.xlsx) or CSV file with athlete data. The file should contain columns for student ID, name, height, weight, and performance metrics.
          </p>
          
          {/* Download Sample Button */}
          <a
            href="/bihar/bihar_sample_data_50_athletes.xlsx"
            download="bihar_sample_data_50_athletes.xlsx"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/50 text-blue-400 rounded-lg transition-colors mb-4"
          >
            <Download className="w-4 h-4" />
            <span className="text-sm font-medium">Download Sample File (50 athletes)</span>
          </a>
          
          <label className="block">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              disabled={importing}
              className="hidden"
            />
            <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
              <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">Click to upload</p>
              <p className="text-sm text-slate-400">or drag and drop</p>
              <p className="text-xs text-slate-500 mt-2">XLSX, XLS, or CSV (max 10MB)</p>
            </div>
          </label>
        </div>

        {/* MongoDB Connection */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">MongoDB Connection</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Connect to a MongoDB database to import athlete data directly.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-slate-400 mb-1">MongoDB URI</label>
              <input
                type="text"
                value={mongoConfig.uri}
                onChange={(e) => setMongoConfig({ ...mongoConfig, uri: e.target.value })}
                placeholder="mongodb://localhost:27017"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={importing}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Database Name</label>
              <input
                type="text"
                value={mongoConfig.database}
                onChange={(e) => setMongoConfig({ ...mongoConfig, database: e.target.value })}
                placeholder="bihar_sports"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={importing}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Collection Name</label>
              <input
                type="text"
                value={mongoConfig.collection}
                onChange={(e) => setMongoConfig({ ...mongoConfig, collection: e.target.value })}
                placeholder="athletes"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={importing}
              />
            </div>
            <button
              onClick={handleMongoConnect}
              disabled={importing || !mongoConfig.uri || !mongoConfig.database || !mongoConfig.collection}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Database className="w-5 h-5" />
              <span>Connect & Import</span>
            </button>
          </div>
        </div>
      </div>

      {/* Import Status */}
      {importing && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
            <p className="text-white">Importing and normalizing data...</p>
          </div>
        </div>
      )}

      {/* Import Result */}
      {result && (
        <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border ${result.success ? 'border-green-700' : 'border-red-700'} p-6`}>
          <div className="flex items-start space-x-3">
            {result.success ? (
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            ) : (
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
            )}
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${result.success ? 'text-green-400' : 'text-red-400'} mb-2`}>
                {result.success ? 'Import Successful' : 'Import Failed'}
              </h3>
              {result.success ? (
                <div className="space-y-2">
                  <p className="text-slate-300">
                    Successfully imported <span className="font-bold text-white">{result.imported_count}</span> athletes
                  </p>
                  {result.outliers_detected > 0 && (
                    <p className="text-yellow-400">
                      ⚠️ Detected {result.outliers_detected} outliers that have been flagged for review
                    </p>
                  )}
                  {result.normalized_count > 0 && (
                    <p className="text-blue-400">
                      ✓ Normalized {result.normalized_count} values (800m times, vertical jumps, etc.)
                    </p>
                  )}
                  <p className="text-sm text-slate-400 mt-3">
                    Dataset: <span className="text-white font-medium">{result.dataset_name}</span>
                  </p>
                </div>
              ) : (
                <p className="text-slate-300">{result.error || 'An error occurred during import'}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live Datasets */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Database className="w-5 h-5 text-blue-400" />
            <span>Imported Datasets</span>
          </h3>
          <button
            onClick={loadDatasets}
            disabled={loadingDatasets}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh datasets"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loadingDatasets ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {loadingDatasets ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
          </div>
        ) : datasets.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No datasets imported yet</p>
            <p className="text-sm mt-1">Upload a file to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {datasets.map((dataset) => {
              const outlierCount = dataset.athlete_data?.filter(a => 
                a.outlier_flags && Object.values(a.outlier_flags).some(f => f.isOutlier)
              ).length || 0;
              const normalizedCount = dataset.athlete_data?.filter(a => 
                a.outlier_flags && Object.values(a.outlier_flags).some(f => f.wasNormalized)
              ).length || 0;
              
              return (
                <div key={dataset.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">{dataset.name}</h4>
                      <p className="text-sm text-slate-400">
                        {new Date(dataset.created_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteDataset(dataset.id)}
                      className="p-2 hover:bg-red-900/30 rounded-lg transition-colors group"
                      title="Delete dataset"
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 group-hover:text-red-400" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded p-2">
                      <p className="text-blue-400 font-semibold">{dataset.record_count}</p>
                      <p className="text-slate-400 text-xs">Athletes</p>
                    </div>
                    <div className="bg-yellow-900/20 border border-yellow-700/30 rounded p-2">
                      <p className="text-yellow-400 font-semibold">{outlierCount}</p>
                      <p className="text-slate-400 text-xs">Outliers</p>
                    </div>
                    <div className="bg-green-900/20 border border-green-700/30 rounded p-2">
                      <p className="text-green-400 font-semibold">{normalizedCount}</p>
                      <p className="text-slate-400 text-xs">Normalized</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center space-x-2">
                    <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full" 
                        style={{ width: `${Math.min(100, (dataset.record_count / 100) * 100)}%` }}
                      ></div>
                    </div>
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Data Format Requirements</h3>
        <div className="space-y-3 text-sm text-slate-300">
          <div>
            <p className="font-medium text-white mb-1">Required Columns:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Student ID or Sl No</li>
              <li>Athlete Name or Student Name</li>
              <li>Height (in cm)</li>
              <li>Weight (in kg)</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-white mb-1">Optional Performance Metrics:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Vertical Jump (cm)</li>
              <li>Standing Broad Jump (cm)</li>
              <li>30m Sprint (seconds)</li>
              <li>Shuttle Run (seconds)</li>
              <li>Football Throw (meters)</li>
              <li>800m Run (mm:ss or seconds)</li>
            </ul>
          </div>
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 mt-4">
            <p className="font-medium text-blue-400 mb-1">Automatic Normalization:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-300 text-xs">
              <li>800m times in Excel decimal format will be converted to mm:ss</li>
              <li>Vertical jump values like 290 → 29.0 cm, 3 → 30.0 cm</li>
              <li>Outliers will be flagged but not rejected</li>
              <li>Percentiles calculated automatically</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
